import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const login = async (email, password) => {
  const response = await api.post('/api/auth/login', { email, password });
  return response.data.data; // Backend returns { success, data: { token, user } }
};

export const logout = async () => {
  const response = await api.post('/api/auth/logout');
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/api/auth/me');
  return response.data.data; // Backend returns { success, data: user }
};

// Leads API
export const getLeads = async (filters = {}) => {
  const response = await api.get('/api/leads', { params: filters });
  return response.data.data || response.data; // Handle both formats
};

export const getLeadById = async (id) => {
  const response = await api.get(`/api/leads/${id}`);
  return response.data;
};

export const getLead = async (id) => {
  const response = await api.get(`/api/leads/${id}`);
  return response.data.data || response.data;
};

export const createLead = async (leadData) => {
  const response = await api.post('/api/leads', leadData);
  return response.data;
};

export const updateLead = async (id, leadData) => {
  const response = await api.put(`/api/leads/${id}`, leadData);
  return response.data;
};

export const deleteLead = async (id) => {
  const response = await api.delete(`/api/leads/${id}`);
  return response.data;
};

export const bulkDeleteLeads = async (leadIds) => {
  const response = await api.delete('/api/leads/bulk', {
    data: { lead_ids: leadIds }
  });
  return response.data;
};

export const bulkUpdateLeads = async (leadIds, updates, options = {}) => {
  const { appendMessageTypes = false } = options;
  const url = appendMessageTypes ? '/api/leads/bulk?append_message_types=true' : '/api/leads/bulk';
  const response = await api.put(url, {
    lead_ids: leadIds,
    updates: updates
  });
  return response.data;
};

export const updateLeadStatus = async (id, statusId) => {
  const response = await api.patch(`/api/leads/${id}/status`, { status_id: statusId });
  return response.data;
};

export const markAttendance = async (id, attendanceStatus, healthDeclaration = null, eventId = null) => {
  const response = await api.patch(`/api/leads/${id}/attendance`, {
    attendance_status: attendanceStatus,
    health_declaration: healthDeclaration,
    event_id: eventId
  });
  return response.data;
};

export const confirmFromWaitingList = async (leadId, eventId) => {
  const response = await api.post(`/api/leads/${leadId}/confirm-from-waitlist`, {
    eventId
  });
  return response.data;
};

// Statuses API
export const getStatuses = async () => {
  const response = await api.get('/api/statuses');
  return response.data.data || response.data; // Handle both formats
};

export const createStatus = async (statusData) => {
  const response = await api.post('/api/statuses', statusData);
  return response.data;
};

export const updateStatus = async (id, statusData) => {
  const response = await api.put(`/api/statuses/${id}`, statusData);
  return response.data;
};

export const deleteStatus = async (id) => {
  const response = await api.delete(`/api/statuses/${id}`);
  return response.data;
};

// Fields API
export const getFields = async () => {
  const response = await api.get('/api/fields');
  return response.data;
};

export const createField = async (fieldData) => {
  const response = await api.post('/api/fields', fieldData);
  return response.data;
};

export const updateField = async (id, fieldData) => {
  const response = await api.put(`/api/fields/${id}`, fieldData);
  return response.data;
};

export const deleteField = async (id) => {
  const response = await api.delete(`/api/fields/${id}`);
  return response.data;
};

// Activity Log API
export const getLeadActivity = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/activity`);
  return response.data;
};

export const getLeadActivities = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/activity`);
  return response.data.data || response.data;
};

export const checkDuplicates = async (phone) => {
  const response = await api.get(`/api/leads/check-duplicates/${encodeURIComponent(phone)}`);
  return response.data.data;
};

// Users API (Admin only)
export const getUsers = async () => {
  const response = await api.get('/api/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/api/users', userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/api/users/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/api/users/${id}`);
  return response.data;
};

// Views API
export const getViews = async () => {
  const response = await api.get('/api/views');
  return response.data;
};

export const getViewById = async (id) => {
  const response = await api.get(`/api/views/${id}`);
  return response.data;
};

export const createView = async (viewData) => {
  const response = await api.post('/api/views', viewData);
  return response.data;
};

export const updateView = async (id, viewData) => {
  const response = await api.put(`/api/views/${id}`, viewData);
  return response.data;
};

export const deleteView = async (id) => {
  const response = await api.delete(`/api/views/${id}`);
  return response.data;
};

export const setDefaultView = async (id) => {
  const response = await api.put(`/api/views/${id}/default`);
  return response.data;
};

// Events API
export const getEvents = async (filters = {}) => {
  const response = await api.get('/api/events', { params: filters });
  return response.data.data || response.data;
};

export const getEventById = async (id) => {
  const response = await api.get(`/api/events/${id}`);
  return response.data.data || response.data;
};

export const createEvent = async (eventData) => {
  const response = await api.post('/api/events', eventData);
  return response.data;
};

export const updateEvent = async (id, eventData) => {
  const response = await api.put(`/api/events/${id}`, eventData);
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await api.delete(`/api/events/${id}`);
  return response.data;
};

export const getEventLeads = async (id) => {
  const response = await api.get(`/api/events/${id}/leads`);
  return response.data.data || response.data;
};

// Lead Event History API
export const getLeadEventHistory = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/event-history`);
  return response.data.data || response.data;
};

// Message Types API
export const getMessageTypes = async () => {
  const response = await api.get('/api/message-types');
  return response.data.data || [];
};

export const getMessageTypeById = async (id) => {
  const response = await api.get(`/api/message-types/${id}`);
  return response.data.data;
};

export const createMessageType = async (messageTypeData) => {
  const response = await api.post('/api/message-types', messageTypeData);
  return response.data;
};

export const updateMessageType = async (id, messageTypeData) => {
  const response = await api.put(`/api/message-types/${id}`, messageTypeData);
  return response.data;
};

export const deleteMessageType = async (id) => {
  const response = await api.delete(`/api/message-types/${id}`);
  return response.data;
};

// Activities API
export const getActivities = async (activeOnly = false) => {
  const response = await api.get('/api/activities', {
    params: activeOnly ? { active_only: 'true' } : {}
  });
  return response.data.data;
};

export const getActivityById = async (id) => {
  const response = await api.get(`/api/activities/${id}`);
  return response.data.data;
};

export const createActivity = async (activityData) => {
  const response = await api.post('/api/activities', activityData);
  return response.data.data;
};

export const updateActivity = async (id, activityData) => {
  const response = await api.put(`/api/activities/${id}`, activityData);
  return response.data.data;
};

export const deleteActivity = async (id) => {
  const response = await api.delete(`/api/activities/${id}`);
  return response.data.data;
};

// Groups API
export const getGroups = async (activeOnly = false) => {
  const response = await api.get('/api/groups', {
    params: activeOnly ? { active_only: 'true' } : {}
  });
  return response.data.data;
};

export const getGroupById = async (id) => {
  const response = await api.get(`/api/groups/${id}`);
  return response.data.data;
};

export const createGroup = async (groupData) => {
  const response = await api.post('/api/groups', groupData);
  return response.data.data;
};

export const updateGroup = async (id, groupData) => {
  const response = await api.put(`/api/groups/${id}`, groupData);
  return response.data.data;
};

export const deleteGroup = async (id) => {
  const response = await api.delete(`/api/groups/${id}`);
  return response.data;
};

export const getGroupMembers = async (id) => {
  const response = await api.get(`/api/groups/${id}/members`);
  return response.data.data;
};

export const assignGroupToEvent = async (groupId, eventId) => {
  const response = await api.post(`/api/groups/${groupId}/assign-event`, { event_id: eventId });
  return response.data;
};

export const unassignGroupFromEvent = async (groupId, eventId) => {
  const response = await api.post(`/api/groups/${groupId}/unassign-event`, { event_id: eventId });
  return response.data;
};

// Dashboard API
export const getDashboardInsights = async (activityId = null) => {
  const params = {};
  if (activityId) {
    params.activity_id = activityId;
  }
  console.log('getDashboardInsights called with activityId:', activityId);
  console.log('Request params:', params);
  const response = await api.get('/api/dashboard/insights', { params });
  console.log('getDashboardInsights response:', response.data);
  return response.data.data;
};

// Calendar API
export const getCalendarEvents = async (filters = {}) => {
  const response = await api.get('/api/calendar/events', { params: filters });
  return response.data.data || response.data;
};

export const getCalendarStats = async (filters = {}) => {
  const response = await api.get('/api/calendar/stats', { params: filters });
  return response.data.data || response.data;
};

export const getMonthEvents = async (year, month) => {
  const response = await api.get(`/api/calendar/month/${year}/${month}`);
  return response.data.data || response.data;
};

export default api;
